import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NativeSelect,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api, errorMessage, isAudienceDoubleOptIn, isInvalidEspCredentials } from '../api/client';
import type {
  BrandSummary,
  EmailList,
  EspAudience,
  EspConnection,
  EspDoubleOptInWarning,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';
import { ReadList, ReadRow } from '../components/ReadValue';
import { whenLabel } from '../format';

/** What each connection status means, in words rather than the code. */
function connectionStatusLabel(status: EspConnection['status']): string {
  if (status === 'invalid_credentials') return 'Mailchimp has stopped accepting this key';
  if (status === 'error') return 'The last attempt to reach Mailchimp failed';
  return 'Connected';
}

/**
 * What an audience option says in the picker.
 *
 * Both the member count and the double opt-in flag are null when Mailchimp did not answer, which
 * is a different thing from a zero or a false, so a null one is left out of the sentence rather
 * than guessed at.
 */
function audienceOptionLabel(audience: EspAudience): string {
  const count =
    audience.member_count === null
      ? ''
      : ` (${audience.member_count.toLocaleString('en-GB')} members)`;
  const warning = audience.double_optin === true ? ', double opt-in is on' : '';
  return `${audience.name}${count}${warning}`;
}

/**
 * Handing confirmed signups to Mailchimp, without ever sending anything ourselves.
 *
 * Quizee collects a newsletter signup and confirms it: a reader who has agreed and, where the
 * publisher asks for it, opened a confirmation email. It does not send a newsletter to anybody.
 * What used to take a developer, a webhook or a CSV now takes an owner pasting a Mailchimp key
 * once and picking an audience per list, and that is the whole of what this screen does.
 *
 * Three things matter more than the rest of the screen:
 *
 * - The key is a credential. It is never shown back, never sits in a value attribute and is never
 *   logged; only its last four characters travel anywhere, in `masked_key`, and reconnecting
 *   means pasting the whole key again rather than editing a value that was never here.
 * - Mailchimp double opt-ins new joiners by default, and quizee has already confirmed this reader
 *   once. Leaving Mailchimp's own confirmation on means a second email most readers never open,
 *   so they sit in Mailchimp as unconfirmed and never actually join the audience, even though
 *   they are a subscriber here already. That is explained in full below the connection, not as a
 *   note beside something else, and mapping a list to an audience that still does this needs a
 *   box ticked first.
 * - `invalid_credentials` and a connection's own `last_error` both get a sentence about where to
 *   find the right key in Mailchimp, never a code.
 */
export function NewsletterSync() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [connection, setConnection] = useState<EspConnection | null | undefined>(undefined);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const [audiences, setAudiences] = useState<readonly EspAudience[] | null>(null);
  const [audiencesProblem, setAudiencesProblem] = useState<string | null>(null);

  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const load = useCallback(() => {
    if (orgId === null) return;
    setLoadProblem(null);
    api
      .espConnection(orgId)
      .then((body) => setConnection(body.connection))
      .catch((error: unknown) => setLoadProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  const loadAudiences = useCallback(() => {
    if (orgId === null || connection === null || connection === undefined) return;
    setAudiencesProblem(null);
    setAudiences(null);
    api
      .espAudiences(orgId)
      .then((body) => setAudiences(body.audiences))
      .catch((error: unknown) => setAudiencesProblem(errorMessage(error)));
  }, [orgId, connection]);

  useEffect(loadAudiences, [loadAudiences]);

  async function connect(apiKey: string): Promise<boolean> {
    if (orgId === null) return false;
    setProblem(null);
    try {
      const body = await api.connectEsp(orgId, apiKey.trim());
      setConnection(body.connection);
      notifications.show({ message: 'Mailchimp connected.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(
        isInvalidEspCredentials(error)
          ? 'Mailchimp did not accept that key. In Mailchimp, go to Account, then Extras, then API keys, and copy the key from there, or make a new one and paste that in instead.'
          : errorMessage(error),
      );
      return false;
    }
  }

  async function disconnect() {
    if (orgId === null) return;
    setProblem(null);
    setDisconnecting(true);
    try {
      await api.disconnectEsp(orgId);
      setConnection(null);
      setAudiences(null);
      setConfirmingDisconnect(false);
      notifications.show({ message: 'Mailchimp disconnected.', color: 'violet' });
    } catch (error) {
      setProblem(errorMessage(error));
      setConfirmingDisconnect(false);
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <>
      <Title order={1} size="h2">
        Mailchimp
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Quizee collects newsletter signups and confirms them itself; it never sends a newsletter.
        This is the handoff: paste a Mailchimp key once, then pick which Mailchimp audience each of{' '}
        {orgName}'s lists sends its confirmed signups to. From then on, a reader who signs up and
        confirms lands in that audience automatically, ready for whatever you already send with
        Mailchimp.
      </Text>

      {!isOwner && (
        <Text c="dimmed" size="sm" mb="md">
          Only an owner can connect Mailchimp, pick an audience for a list or disconnect it, because
          it decides where every reader who signs up ends up. Ask one of the owners of {orgName} if
          it needs to change.
        </Text>
      )}

      {loadProblem !== null && <ErrorNote message={loadProblem} onRetry={load} />}
      {loadProblem === null && connection === undefined && (
        <Loading label="Loading your Mailchimp connection" />
      )}
      {problem !== null && <ErrorNote message={problem} />}

      {connection !== undefined && (
        <Card withBorder radius="lg" p="lg" component="section">
          {connection === null && !isOwner ? (
            <Text c="dimmed">
              Nothing is connected, so confirmed signups stay in quizee only. The subscribers screen
              and a CSV export are the other two ways to get at them.
            </Text>
          ) : (
            <ReadEdit<{ apiKey: string }>
              noun={connection === null ? 'the Mailchimp connection' : 'the Mailchimp key'}
              canEdit={isOwner}
              open={() => ({ apiKey: '' })}
              openLabel={connection === null ? 'Connect Mailchimp' : 'Reconnect with a new key'}
              openName={
                connection === null ? 'Connect Mailchimp' : 'Reconnect Mailchimp with a new key'
              }
              saveLabel="Connect"
              saveName="Connect Mailchimp"
              busyLabel="Connecting..."
              problemsTitle="Before Mailchimp can be connected:"
              check={(draft) =>
                draft.apiKey.trim() === '' ? ['Paste your Mailchimp API key.'] : []
              }
              save={(draft) => connect(draft.apiKey)}
              form={(draft, set) => (
                <PasswordInput
                  id="esp-api-key"
                  label="Mailchimp API key"
                  description="Find it in Mailchimp under Account, then Extras, then API keys. Pasting a new key here replaces whatever key is on file."
                  placeholder="paste your key"
                  value={draft.apiKey}
                  autoComplete="off"
                  maxLength={200}
                  onChange={(event) => set({ apiKey: event.currentTarget.value })}
                  style={{ maxWidth: '28rem' }}
                />
              )}
            >
              {connection === null ? (
                <Text c="dimmed">
                  Nothing is connected, so confirmed signups stay in quizee only. Connecting takes
                  one key from Mailchimp; nothing sends until you have also picked an audience for a
                  list further down this screen.
                </Text>
              ) : (
                <ReadList>
                  <ReadRow
                    label="Key"
                    value={`Ends ${connection.masked_key}`}
                    note="Only the last four characters are ever kept on screen. Reconnecting replaces the whole key; there is no way to see the rest of it again."
                  />
                  <ReadRow label="Connected" value={whenLabel(connection.connected_at)} />
                  <ReadRow
                    label="Status"
                    value={connectionStatusLabel(connection.status)}
                    note={
                      // Never the provider's own words: a code or a quoted error is not a
                      // sentence an editor can act on, so the fixed wording per status is all
                      // that is ever shown here, whatever last_error itself actually says.
                      connection.status === 'invalid_credentials'
                        ? 'This almost always means the key was revoked in Mailchimp. Paste a fresh one in above to fix it.'
                        : connection.status === 'error'
                          ? 'This is usually Mailchimp being unreachable rather than anything wrong here, and it should clear on its own. If it does not, check that the key still has access in Mailchimp.'
                          : undefined
                    }
                  />
                  <ReadRow
                    label="Last synced"
                    value={
                      connection.last_synced_at === null
                        ? 'Nothing has synced yet'
                        : whenLabel(connection.last_synced_at)
                    }
                  />
                </ReadList>
              )}
            </ReadEdit>
          )}

          {isOwner && connection !== null && !confirmingDisconnect && (
            <Button
              variant="subtle"
              color="red"
              mt="md"
              onClick={() => {
                setProblem(null);
                setConfirmingDisconnect(true);
              }}
            >
              Disconnect Mailchimp
            </Button>
          )}

          {confirmingDisconnect && (
            <Card withBorder radius="md" p="md" mt="md">
              <Text size="sm" fw={500}>
                Disconnect Mailchimp for {orgName}?
              </Text>
              <Text size="sm" mt={4}>
                Every list stops sending to Mailchimp from the moment you press this. Nothing
                already sent to Mailchimp is removed there, and nothing collected here is lost
                either: confirmed signups carry on being collected as normal. Reconnecting later
                means pasting a key again and picking an audience for every list again.
              </Text>
              <Group mt="sm">
                <Button
                  color="red"
                  size="compact-sm"
                  disabled={disconnecting}
                  onClick={() => void disconnect()}
                  autoFocus
                >
                  {disconnecting ? 'Disconnecting...' : 'Yes, disconnect it'}
                </Button>
                <Button
                  variant="default"
                  size="compact-sm"
                  disabled={disconnecting}
                  onClick={() => setConfirmingDisconnect(false)}
                >
                  Keep it connected
                </Button>
              </Group>
            </Card>
          )}
        </Card>
      )}

      {connection !== null &&
        connection !== undefined &&
        connection.audiences_double_optin_warning !== null && (
          <DoubleOptInWarning
            warning={connection.audiences_double_optin_warning}
            checkedAt={connection.audiences_checked_at}
          />
        )}

      {connection !== null && connection !== undefined && (
        <ListMappings
          orgId={orgId ?? ''}
          orgName={orgName}
          isOwner={isOwner}
          audiences={audiences}
          audiencesProblem={audiencesProblem}
          onRetryAudiences={loadAudiences}
        />
      )}
    </>
  );
}

/**
 * The most important thing on this screen.
 *
 * Not a note beside something else: its own card, with what actually happens and exactly where to
 * go in Mailchimp to stop it. The explanation itself is the API's own, in an editor's words, the
 * same sentence a mapping or a backfill gets when it runs into the same setting: written once,
 * rather than a second version living here that could drift from it. What this card adds is the
 * loud presentation, the list of which audiences it actually applies to, and how stale the check
 * behind it is, since `audiences_checked_at` is Mailchimp's setting as it was read, not as it is
 * this moment.
 */
function DoubleOptInWarning({
  warning,
  checkedAt,
}: {
  warning: EspDoubleOptInWarning;
  checkedAt: string | null;
}) {
  return (
    <Card
      withBorder
      radius="lg"
      p="lg"
      my="md"
      component="section"
      role="alert"
      style={{ borderColor: 'var(--mantine-color-yellow-6)', borderWidth: 2 }}
    >
      <Title order={2} size="h4">
        {warning.audiences.length === 1
          ? 'This Mailchimp audience will lose you subscribers'
          : 'These Mailchimp audiences will lose you subscribers'}
      </Title>
      <Text size="sm" mt="sm">
        {warning.message}
      </Text>
      {warning.audiences.length > 0 && (
        <Text size="sm" mt="sm" component="ul" style={{ paddingLeft: '1.2rem', margin: 0 }}>
          {warning.audiences.map((audience) => (
            <li key={audience.id}>{audience.name}</li>
          ))}
        </Text>
      )}
      <Text size="sm" mt="sm" c="dimmed">
        Checked {checkedAt === null ? 'when Mailchimp was last asked' : whenLabel(checkedAt)}.
        Mapping a list to one of these still needs a box ticked first.
      </Text>
    </Card>
  );
}

/** What a list's mapping form holds while it is open. */
interface MappingDraft {
  readonly audienceId: string;
  /** Ticked only when the chosen audience still double opt-ins, to say it was read, not ignored. */
  readonly acceptDoubleOptIn: boolean;
}

/**
 * The lists on each brand, and which Mailchimp audience each one sends its confirmed signups to.
 *
 * A brand picker rather than one long list across every brand, for the same reason the Lists
 * screen has one: a masthead's briefings are its own, so choosing which to look at first is the
 * useful question rather than showing every list on every title at once.
 */
function ListMappings({
  orgId,
  orgName,
  isOwner,
  audiences,
  audiencesProblem,
  onRetryAudiences,
}: {
  orgId: string;
  orgName: string;
  isOwner: boolean;
  audiences: readonly EspAudience[] | null;
  audiencesProblem: string | null;
  onRetryAudiences: () => void;
}) {
  const [brands, setBrands] = useState<readonly BrandSummary[] | null>(null);
  const [brandId, setBrandId] = useState('');
  const [brandsProblem, setBrandsProblem] = useState<string | null>(null);

  const [lists, setLists] = useState<readonly EmailList[] | null>(null);
  const [listsProblem, setListsProblem] = useState<string | null>(null);

  useEffect(() => {
    if (orgId === '') return;
    setBrandsProblem(null);
    api
      .brands(orgId)
      .then((body) => {
        setBrands(body.brands);
        const first = body.brands[0];
        setBrandId((current) => (current === '' && first !== undefined ? first.brand_id : current));
      })
      .catch((error: unknown) => setBrandsProblem(errorMessage(error)));
  }, [orgId]);

  const loadLists = useCallback(() => {
    if (orgId === '' || brandId === '') return;
    setListsProblem(null);
    setLists(null);
    api
      .lists(orgId, brandId)
      .then((body) => setLists(body.lists.filter((list) => !list.archived)))
      .catch((error: unknown) => setListsProblem(errorMessage(error)));
  }, [orgId, brandId]);

  useEffect(loadLists, [loadLists]);

  function replaceList(next: EmailList) {
    setLists((current) =>
      current === null
        ? current
        : current.map((list) => (list.list_id === next.list_id ? next : list)),
    );
  }

  const brandName = brands?.find((brand) => brand.brand_id === brandId)?.name ?? 'this brand';

  return (
    <>
      <Title order={2} size="h3" mt="xl">
        Send a list to Mailchimp
      </Title>
      <Text size="sm" c="dimmed" mt={4} mb="md">
        Each list on {orgName} can send its confirmed signups to one Mailchimp audience. A list with
        nothing picked stays exactly as it is: collected here, and nowhere else.
      </Text>

      {brandsProblem !== null && <ErrorNote message={brandsProblem} />}

      {brands !== null && brands.length > 0 && (
        <NativeSelect
          id="esp-mapping-brand"
          label="Brand"
          value={brandId}
          onChange={(event) => setBrandId(event.target.value)}
          data={brands.map((brand) => ({ value: brand.brand_id, label: brand.name }))}
          style={{ maxWidth: '20rem' }}
          mb="lg"
        />
      )}

      {brands !== null && brands.length === 0 && (
        <Text c="dimmed">There are no brands yet, so there is nothing with a list on it.</Text>
      )}

      {audiencesProblem !== null && (
        <ErrorNote
          message={`Mailchimp's audiences could not be loaded, so no list can be pointed at one right now. ${audiencesProblem}`}
          onRetry={onRetryAudiences}
        />
      )}

      {listsProblem !== null && <ErrorNote message={listsProblem} onRetry={loadLists} />}
      {listsProblem === null && lists === null && brandId !== '' && (
        <Loading label={`Loading ${brandName}'s lists`} />
      )}

      {lists !== null && lists.length === 0 && (
        <Text c="dimmed">No current lists on {brandName}.</Text>
      )}

      {lists !== null && lists.length > 0 && (
        <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {lists.map((list) => (
            <ListMappingCard
              key={list.list_id}
              orgId={orgId}
              brandId={brandId}
              list={list}
              canEdit={isOwner}
              audiences={audiences}
              onChanged={replaceList}
            />
          ))}
        </Stack>
      )}
    </>
  );
}

function ListMappingCard({
  orgId,
  brandId,
  list,
  canEdit,
  audiences,
  onChanged,
}: {
  orgId: string;
  brandId: string;
  list: EmailList;
  canEdit: boolean;
  audiences: readonly EspAudience[] | null;
  onChanged: (list: EmailList) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmingUnmap, setConfirmingUnmap] = useState(false);
  const [unmapping, setUnmapping] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfilled, setBackfilled] = useState(false);
  const [hasSubscribed, setHasSubscribed] = useState(false);
  /**
   * A warning that arrived with a save rather than before it.
   *
   * Checked against Mailchimp at the moment this list was actually mapped, which can catch what
   * the picker's own cached audience list cannot: an audience that had double opt-in switched on
   * since that list was last loaded. Kept until dismissed, the same way the mapping warning above
   * is, because it is exactly as worth reading.
   */
  const [freshWarning, setFreshWarning] = useState<EspDoubleOptInWarning | null>(null);

  const mapping = list.esp_sync ?? null;

  useEffect(() => {
    if (mapping === null) {
      setHasSubscribed(false);
      return;
    }
    let live = true;
    api
      .subscriptionSummary(orgId, {
        brand_id: brandId,
        list_id: list.list_id,
        status: 'subscribed',
      })
      .then((summary) => {
        if (live) setHasSubscribed(summary.total > 0);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [orgId, brandId, list.list_id, mapping]);

  async function saveMapping(draft: MappingDraft): Promise<boolean> {
    setProblem(null);
    try {
      const body = await api.syncList(orgId, brandId, list.list_id, draft.audienceId);
      onChanged({ ...list, esp_sync: body.mapping });
      setFreshWarning(body.double_optin_warning);
      notifications.show({ message: `${list.name} is now sent to Mailchimp.`, color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  async function unmap() {
    setProblem(null);
    setUnmapping(true);
    try {
      await api.unsyncList(orgId, brandId, list.list_id);
      // esp_sync must be absent when a list is not mapped, never present and null, so it is left
      // out of the copy rather than cleared to a value the API would never itself send.
      const { esp_sync, ...withoutSync } = list;
      onChanged(withoutSync);
      setConfirmingUnmap(false);
      setFreshWarning(null);
      notifications.show({
        message: `${list.name} stopped sending to Mailchimp.`,
        color: 'violet',
      });
    } catch (error) {
      setProblem(errorMessage(error));
      setConfirmingUnmap(false);
    } finally {
      setUnmapping(false);
    }
  }

  async function backfill() {
    setProblem(null);
    setBackfilling(true);
    try {
      const body = await api.backfillListSync(orgId, brandId, list.list_id);
      setBackfilled(true);
      notifications.show({
        message: `Existing subscribers on ${list.name} are being sent to ${body.audience.name} now.`,
        color: 'violet',
      });
    } catch (error) {
      // Checked fresh against Mailchimp at the moment of the request, because an audience can
      // have double opt-in switched on long after this list was mapped. The API's own message
      // already says what happened and what to change in Mailchimp's own words, the same sentence
      // the connection warning uses, so it is shown as it stands rather than written a second way.
      setProblem(
        isAudienceDoubleOptIn(error)
          ? `${errorMessage(error)} Try the sync again once that is off.`
          : errorMessage(error),
      );
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <Card component="li" withBorder radius="md" p="md">
      {problem !== null && <ErrorNote message={problem} />}

      <ReadEdit<MappingDraft>
        noun={`the Mailchimp audience for "${list.name}"`}
        canEdit={canEdit && audiences !== null}
        open={() => ({ audienceId: mapping?.audience_id ?? '', acceptDoubleOptIn: false })}
        openLabel={mapping === null ? 'Send to Mailchimp' : 'Change'}
        openName={
          mapping === null
            ? `Send ${list.name} to Mailchimp`
            : `Change the Mailchimp audience for ${list.name}`
        }
        saveLabel="Save"
        saveName={`Save the Mailchimp audience for ${list.name}`}
        problemsTitle="Before this can be saved:"
        check={(draft) => {
          if (draft.audienceId === '') return ['Pick an audience to send this list to.'];
          const chosen = audiences?.find((audience) => audience.id === draft.audienceId);
          if (chosen?.double_optin === true && !draft.acceptDoubleOptIn) {
            return [
              `${chosen.name} still double opt-ins new joiners. Tick the box below to say you have read what that means, or turn double opt-in off for it in Mailchimp first.`,
            ];
          }
          return [];
        }}
        save={saveMapping}
        form={(draft, set) => {
          const chosen = audiences?.find((audience) => audience.id === draft.audienceId);
          return (
            <Stack gap="sm">
              <NativeSelect
                id={`esp-audience-${list.list_id}`}
                label="Send confirmed signups on this list to"
                value={draft.audienceId}
                onChange={(event) =>
                  set({ audienceId: event.target.value, acceptDoubleOptIn: false })
                }
                data={[
                  { value: '', label: 'Choose an audience' },
                  ...(audiences ?? []).map((audience) => ({
                    value: audience.id,
                    label: audienceOptionLabel(audience),
                  })),
                ]}
                style={{ maxWidth: '28rem' }}
              />
              {chosen?.double_optin === true && (
                <Card
                  withBorder
                  radius="md"
                  p="md"
                  style={{ borderColor: 'var(--mantine-color-yellow-6)' }}
                >
                  <Text size="sm" fw={500}>
                    {chosen.name} still double opt-ins new joiners
                  </Text>
                  <Text size="sm" mt={4}>
                    Quizee has already confirmed this reader once. Sending them here means Mailchimp
                    asks a second time, most will not answer, and they will sit as pending in{' '}
                    {chosen.name} rather than actually joining it.
                  </Text>
                  <Checkbox
                    mt="sm"
                    checked={draft.acceptDoubleOptIn}
                    onChange={(event) =>
                      set({ ...draft, acceptDoubleOptIn: event.currentTarget.checked })
                    }
                    label="I understand, and I want to send this list here anyway"
                  />
                </Card>
              )}
            </Stack>
          );
        }}
      >
        <Group gap="xs" align="center" wrap="wrap">
          <Text fw={500}>{list.name}</Text>
          {mapping !== null && (
            <Badge size="sm" radius="sm" variant="light" color="violet" tt="none">
              Sending to Mailchimp
            </Badge>
          )}
        </Group>
        {mapping === null ? (
          <Text size="sm" c="dimmed" mt={2}>
            Not sent to Mailchimp. Confirmed signups on this list stay in quizee only.
          </Text>
        ) : (
          <ReadList>
            <ReadRow label="Sent to" value={mapping.audience_name ?? 'An audience with no name'} />
            <ReadRow
              label="Last synced"
              value={
                mapping.last_synced_at === null
                  ? 'Not yet: only signups confirmed from now on will be sent'
                  : whenLabel(mapping.last_synced_at)
              }
            />
          </ReadList>
        )}
      </ReadEdit>

      {freshWarning !== null && (
        <Card
          withBorder
          radius="md"
          p="md"
          mt="sm"
          role="alert"
          style={{ borderColor: 'var(--mantine-color-yellow-6)' }}
        >
          <Text size="sm">{freshWarning.message}</Text>
          <Button
            variant="subtle"
            size="compact-sm"
            mt="xs"
            px={0}
            onClick={() => setFreshWarning(null)}
          >
            I have read this
          </Button>
        </Card>
      )}

      {canEdit && mapping !== null && (
        <Group mt="sm" gap="sm">
          {hasSubscribed && !backfilled && (
            <Button
              variant="default"
              size="compact-sm"
              disabled={backfilling}
              onClick={() => void backfill()}
            >
              {backfilling ? 'Sending...' : 'Sync existing subscribers'}
            </Button>
          )}
          {backfilled && (
            <Text size="sm" c="dimmed">
              Existing subscribers are being sent to Mailchimp now.
            </Text>
          )}
          {!confirmingUnmap && (
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              onClick={() => {
                setProblem(null);
                setConfirmingUnmap(true);
              }}
            >
              Stop sending to Mailchimp
            </Button>
          )}
        </Group>
      )}

      {confirmingUnmap && (
        <Card withBorder radius="md" p="md" mt="sm">
          <Text size="sm" fw={500}>
            Stop sending {list.name} to Mailchimp?
          </Text>
          <Text size="sm" mt={4}>
            New confirmed signups on this list stop reaching Mailchimp from the moment you press
            this. Everybody already sent stays exactly where they are in Mailchimp; nothing is
            removed there and nothing here is lost either.
          </Text>
          <Group mt="sm">
            <Button
              color="red"
              size="compact-sm"
              disabled={unmapping}
              onClick={() => void unmap()}
              autoFocus
            >
              {unmapping ? 'Stopping...' : 'Yes, stop sending it'}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              disabled={unmapping}
              onClick={() => setConfirmingUnmap(false)}
            >
              Keep sending it
            </Button>
          </Group>
        </Card>
      )}
    </Card>
  );
}
